import { firstValueFrom } from 'rxjs';

import {
  type ActiveDirectoryAuthApi,
  AuthenticationTokenStorageService,
  type KerberosAttemptOutcome,
} from '@finverity/authentication';

/**
 * Per-tab guard preventing a Kerberos retry loop. Set ONLY when the
 * backend definitively rejected the handshake (4xx outcome) — never on
 * transient failures, so a one-off network blip or 5xx doesn't strand
 * the user in sessionStorage until they close the tab.
 *
 * Cleared on logout via the wholesale `sessionStorage.clear()` call inside
 * `handleLogout` (libs/finverity-authentication/.../authentication.effects.ts),
 * which is triggered by the `logoutSuccess$` effect. A grep for this key
 * therefore won't find an explicit `removeItem` — the wipe is broader by
 * design so a logout drains every session-scoped flag. If that broad clear
 * is ever narrowed, add a targeted `sessionStorage.removeItem(KERBEROS_ATTEMPTED_FLAG_KEY)`
 * there so silent SSO can re-attempt on the next login in the same tab.
 */
export const KERBEROS_ATTEMPTED_FLAG_KEY = 'finverity.kerberos.attempted';

/**
 * URL escape hatch — `?noSso=1` skips the page-load attempt entirely. Useful
 * for support staff debugging, multi-account users on the same machine, and
 * manual password tests against an AD-configured tenant.
 */
const ESCAPE_HATCH_PARAM = 'noSso';

/**
 * Fire a single silent Kerberos handshake at app bootstrap. On success the
 * tokens are stored in the same place as the password-login flow puts them;
 * the bootstrap chain in `app.config.ts` then calls
 * `AuthenticationFacadeService.initAuthUser()`, which sees the freshly
 * stored token and drives the `scheduleRefreshToken` + `initAuthUser`
 * dispatch in one place — avoiding the double-fire that would happen if
 * this initializer also dispatched those actions.
 *
 * Bail-out priority (in order):
 *   1. Token already present → user is logged in or refreshing → leave it.
 *   2. `?noSso=1` in URL → admin override → skip. Any other value is
 *      ignored; the param is matched strictly so `?noSso=0` does NOT skip.
 *   3. `sessionStorage[KERBEROS_ATTEMPTED_FLAG_KEY]` set → backend already
 *      definitively rejected us in this tab → don't loop. Cleared on logout.
 *
 * Outcome handling (after the fetch resolves):
 *   - `success`   → store tokens. No flag — the `getToken()` bail above
 *                   covers subsequent reloads.
 *   - `rejected`  → set the flag and fall through to the email/password form.
 *   - `transient` → leave the flag UNSET so the next reload retries. A
 *                   single network blip, timeout, or 5xx must not silence
 *                   silent SSO for the whole tab session.
 *
 * The Observable from `tryKerberosLogin` never errors — every failure mode
 * is mapped into a `KerberosAttemptOutcome` by the API service — so we
 * don't need a try/catch around `firstValueFrom`. A thrown error here
 * would indicate a programming bug (e.g., broken DI); in that case we
 * still want to fall through silently so the existing /login form takes
 * over rather than blocking bootstrap.
 */
export async function attemptKerberosAutoLogin(
  adAuth: ActiveDirectoryAuthApi,
  tokenStorage: AuthenticationTokenStorageService
): Promise<void> {
  if (tokenStorage.getToken()) return;
  if (new URLSearchParams(window.location.search).get(ESCAPE_HATCH_PARAM) === '1') return;
  if (sessionStorage.getItem(KERBEROS_ATTEMPTED_FLAG_KEY) === '1') return;

  let outcome: KerberosAttemptOutcome;
  try {
    outcome = await firstValueFrom(adAuth.tryKerberosLogin());
  } catch {
    return;
  }

  switch (outcome.kind) {
    case 'success':
      tokenStorage.saveToken(outcome.tokens.access_token);
      tokenStorage.saveRefreshToken(outcome.tokens.refresh_token);
      return;
    case 'rejected':
      sessionStorage.setItem(KERBEROS_ATTEMPTED_FLAG_KEY, '1');
      return;
    case 'transient':
      return;
  }
}
